import { useCallback, useEffect, useState } from "react";
import { createMessage } from "../api/services/messages";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../states";
import { updateHistory } from "../states/slices/chatSlice";
import { Message } from "../types/objectTypes";

const useChatBot = (type: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useSelector((state: RootState) => state.chat?.[type] || []);
  const botStatus = useSelector((state: RootState) => state.chat.status);
  const [messages, setMessages] = useState<Message[]>(history);
  const [isLoading, setIsLoading] = useState(false);
  const conversationId = useSelector(
    (state: RootState) => state.chat.conversationId
  );

  useEffect(() => {
    dispatch(updateHistory({ type, messages }));
  }, [type, messages, dispatch]);

  useEffect(() => {
    switch (botStatus) {
      case "start":
      case "reset":
        setMessages(history);
        return;
    }
  }, [botStatus, history]);

  const handleChatBotSend = useCallback(
    async (input: string, onRecommendation: (key: string) => void) => {
      const onMessage = (text: string, error: boolean = false): void => {
        setMessages((prevMessages) =>
          prevMessages.map((message, index, arr) => {
            const isLast = index === arr.length - 1;

            if (!isLast) return message;

            if (message.role === "error") {
              return error
                ? { role: "error", content: text }
                : { role: "assistant", content: text };
            }

            if (error) {
              return { role: "error", content: text };
            }

            return {
              role: "assistant",
              content: `${message.content}${text}`,
            };
          })
        );
      };

      try {
        await createMessage(
          { id: conversationId, message: input, type },
          onMessage
        );

        const regexMatch = /!\[([^\]]+)\]\([^)]+\)/;
        const surveyEndMatch = /Thank you for your honest feedback/;

        setMessages((prevMessages) => {
          if (!prevMessages.length) return prevMessages;

          const lastMessage =
            prevMessages[prevMessages.length - 1]?.content || "";

          const match = lastMessage.match(regexMatch);
          const surveyEnd = lastMessage.match(surveyEndMatch);
          const key = match?.[1] || "";

          if (key) {
            const formattedKey = key.replace(/[^a-zA-Z0-9\s()]/g, "");

            onRecommendation(formattedKey);
          } else if (surveyEnd) {
            onRecommendation("survey.end");
          }

          return prevMessages;
        });
      } catch (error) {
        console.error("Error sending message:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [conversationId, type]
  );

  const resumeSend = useCallback(
    async (onRecommendation: (key: string) => void) => {
      const lastMessage = messages[messages.length - 1];
      const lastUserMessage = messages[messages.length - 2];

      if (
        (lastMessage?.role === "assistant" && lastMessage?.content === "") ||
        lastMessage?.role === "error"
      ) {
        setIsLoading(true);
        handleChatBotSend(lastUserMessage.content, onRecommendation);
      }
    },
    [handleChatBotSend, messages]
  );

  const send = useCallback(
    async (
      values: { input: string; type: string },
      onRecommendation: (key: string) => void
    ) => {
      const { input } = values;
      const trimmedInput = input.trim();

      if (!trimmedInput) return;

      setIsLoading(true);

      setMessages((prevMessages) => [
        ...prevMessages,
        { role: "user", content: trimmedInput },
        { role: "assistant", content: "" },
      ]);

      handleChatBotSend(trimmedInput, onRecommendation);
    },
    [handleChatBotSend]
  );

  const resetMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    send,
    isLoading,
    resetMessages,
    resumeSend,
  };
};

export default useChatBot;
